import {setWorkItemStatus, log} from "../../ApiCalls";
import {LoadingIcon} from "../../Common";

import {StatusEnum} from "../../../../Types/StatusEnum";

import {azureTokenContext, messageBoxContext, userIdContext} from "../../../../App";

import {Dialog, DialogPanel, DialogTitle, Transition, TransitionChild} from '@headlessui/react'
import React, {Fragment, useContext, useState} from 'react'
import {makeMetricPayloadByPriorityNumber} from "../OpenDocumentDropdown";
import {useLogMetric} from "../../../../hooks/useLogMetric";


const ILLEGAL_TRANSITION = /Illegal to go from status (\w+) to status/;

// The BFF (CloudBerry ServerlessBase) returns 424 with {ErrorMessage: "<downstream message>|<WorkItem message>"}.
const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    const xhr = error as {responseJSON?: {ErrorMessage?: string}, responseText?: string, status?: number} | undefined;
    return xhr?.responseJSON?.ErrorMessage ?? xhr?.responseText ?? (xhr?.status ? `HTTP ${xhr.status}` : "Unknown error");
};

// Returns the current work item status when WorkItem rejected the transition, for example after the Word add-in set it.
const getIllegalTransitionFromStatus = (error: unknown): string | null => {
    const status = (error as {status?: number} | undefined)?.status;
    if (status !== 424 && status !== 400) return null;
    return ILLEGAL_TRANSITION.exec(getErrorMessage(error))?.[1] ?? null;
};

export default function SetToReviewModal({
                                             isOpen,
                                             setIsOpen,
                                             task,
                                             setAllTasks,
                                             setSelectedTasks
                                         }) {
    let setMessageBoxProp = useContext(messageBoxContext);
    const userId = useContext(userIdContext);
    const azureToken = useContext(azureTokenContext);
    const [isPending, setIsPending] = useState(false);
    const logMetric = useLogMetric();

    const confirmAction = async () => {
        if (!task.WorkItemId) {
            // The task has no work item id (for example stale state after a revision). Do not send a request without an id.
            log("ERROR", `Cannot set task to readyForReview: missing WorkItemId for TaskId: ${task.TaskId}`, "AuthorsPortal", task.WorkItemId, "Author", userId, azureToken);
            logMetric({category: "SetToReview", subCategory: "Missing work item id", message: `Set to review blocked: missing WorkItemId for TaskId: ${task.TaskId}`, workItemId: task.WorkItemId, userRole: "Author", status: "Failed", payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
            setMessageBoxPropFunc("ErrorMissingWorkItemId");
            setIsOpen(false);
            return;
        }
        setIsPending(true);
        try {
            const response = await setWorkItemStatus(StatusEnum.readyForReview, task.WorkItemId, userId, azureToken);
            if (response.Success === true) {
                updateTasks();
                log("INFO", `Author set task to readyForReview for WorkItemId: ${task.WorkItemId}`, "AuthorsPortal", task.WorkItemId, "Author", userId, azureToken);
                const payload = makeMetricPayloadByPriorityNumber(task.PriorityNumber);
                logMetric({category: "SetToReview", subCategory: "Task set to review", message: "Author set task to readyForReview", workItemId: task.WorkItemId, userRole: "Author", payload});
                setMessageBoxPropFunc("SuccessSetToReview");
            } else {
                await logFailure(response.Message);
                setMessageBoxPropFunc("ErrorSetToReview");
            }
        } catch (error) {
            const message = getErrorMessage(error);
            const currentStatus = getIllegalTransitionFromStatus(error);
            if (currentStatus) {
                // The task list is stale: the work item already left Init. Show the current status so the button disappears.
                updateTasks(currentStatus === StatusEnum.init ? StatusEnum.readyForReview : currentStatus);
                log("ERROR", `Work item already left Init when author set task to readyForReview for WorkItemId: ${task.WorkItemId}. Message: ${message}`, "AuthorsPortal", task.WorkItemId, "Author", userId, azureToken);
                logMetric({category: "SetToReview", subCategory: "Already in review", message: `Set to review rejected: work item is ${currentStatus} for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: "Author", status: "Failed", payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
                setMessageBoxPropFunc("ErrorAlreadyInReview");
            } else {
                await logFailure(message);
                setMessageBoxPropFunc("ErrorSetToReview");
            }
        } finally {
            setIsPending(false);
            setIsOpen(false);
        }
    }

    const updateTasks = (status: string = StatusEnum.readyForReview) => {
        let newTask = {...task};
        newTask.Status = status;
        newTask.WorkItemStatus = status;
        newTask.DueDate = "";
        newTask.Description = "Vent på innspill fra fagredaktør og korrekturleser";

        // Update the task in allTasks and selectedTasks
        setAllTasks((prevTasks) =>
            prevTasks.map((t) =>
                t.TaskId === newTask.TaskId ? {...t, ...newTask} : t
            )
        );
        setSelectedTasks((prevTasks) =>
            prevTasks.map((t) =>
                t.TaskId === newTask.TaskId ? {...t, ...newTask} : t
            )
        );
    }

    const logFailure = async (message: string) => {
        log("ERROR", `Failed to set task to readyForReview for WorkItemId: ${task.WorkItemId}. Message: ${message}`, "AuthorsPortal", task.WorkItemId, "Author", userId, azureToken);
        const payload = makeMetricPayloadByPriorityNumber(task.PriorityNumber);
        logMetric({category: "SetToReview", subCategory: "Failed to set review", message: `Failed to set task to readyForReview for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: "Author", status: "Failed", payload});
    };

    const setMessageBoxPropFunc = (prop: string) => {
        // since it is invocated by changes in prop, I want to make sure that it changes
        setMessageBoxProp("");
        setTimeout(() => {
            setMessageBoxProp(prop);
        }, 10);
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={() => setIsOpen(false)}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25"/>
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel
                                className="w-full max-w-3xl max-h-96 transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <DialogTitle
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    Sett til vurdering
                                </DialogTitle>
                                <div className="modal-paragraph-container">
                                    <p>
                                        <b>Er du sikker på at du vil sette hele manuset til vurdering?</b>
                                        <br/>
                                        Merk at du kan ha flere utestående oppgaver.
                                        <br/>
                                        <br/>
                                        <b>Har du bare gjennomgått deler av manuset (for eksempel noter påvirket av lovendringer)?</b>
                                        <br/>
                                        Send en e-post til support@karnovgroup.no, så hjelper vi deg med å klippe ut det som er klart.
                                    </p>
                                </div>

                                <div className="mt-4">
                                    <button
                                        type="button"
                                        className="confirm-button"
                                        onClick={() => confirmAction()}
                                        disabled={isPending}
                                    >
                                        {isPending && (
                                            <LoadingIcon/>
                                        )}
                                        Ja, sett hele manuset til vurdering.
                                    </button>
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={() => {
                                            logMetric({category: "SetToReview", subCategory: "Modal cancelled", message: `Author cancelled set to review modal for WorkItemId: ${task.WorkItemId}`, workItemId: task.WorkItemId, userRole: "Author", payload: makeMetricPayloadByPriorityNumber(task.PriorityNumber)});
                                            setIsOpen(false);
                                        }}
                                        disabled={isPending}
                                    >
                                        {isPending && (
                                            <LoadingIcon/>
                                        )}
                                        Avbryt
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

